<script lang="ts" setup>
import { match } from 'ts-pattern';
import DefaultImageNotFound from '~/assets/error-not-found.svg?url';
import DefaultImageSupport from '~/assets/error-support.svg?url';
import DefaultImageCritical from '~/assets/error-critical.svg?url';

type ErrorScreenVariant = 'not-found' | 'critical' | 'support';

const props = withDefaults(defineProps<{
	title?: string;
	subtitle?: string;
	image?: string;
	size?: 'sm' | 'lg' | 'xl';
	variant?: ErrorScreenVariant;
}>(), {
	size: 'lg',
	image: undefined,
	title: undefined,
	subtitle: undefined,
	variant: 'critical',
});

const { t } = useI18n();

const defaultTitle = computed(() => match(props.variant)
	.with('critical', () => t('page.error.critical.title'))
	.with('not-found', () => t('page.error.notFound.title'))
	.with('support', () => t('page.error.support.title'))
	.exhaustive());

const defaultImage = computed(() => match(props.variant)
	.with('critical', () => DefaultImageCritical)
	.with('not-found', () => DefaultImageNotFound)
	.with('support', () => DefaultImageSupport)
	.exhaustive());
</script>

<template>
	<div class="flex w-full flex-col items-center justify-center">
		<img
			:src="image || defaultImage"
			:class="{
				'size-80': size === 'lg' || size === 'xl',
				'size-60': size === 'sm'
			}"
			:alt="title || defaultTitle"
			class="dark:invert dark:opacity-90"
		>
		<p
			class="font-bold"
			:class="{
				'text-3xl': size === 'xl',
				'text-xl': size === 'lg',
				'text-lg': size === 'sm'
			}"
		>
			{{ title || defaultTitle }}
		</p>
		<p
			v-if="subtitle"
			class="text-center text-toned"
			:class="{
				'max-w-xl mt-2': size === 'xl',
				'max-w-lg mt-1': size === 'lg' || size === 'sm',
			}"
		>
			{{ subtitle }}
		</p>
		<div
			v-if="$slots.default"
			class="mt-4"
		>
			<slot />
		</div>
	</div>
</template>
